import React, { useEffect, useContext, useMemo} from 'react';
import PropTypes from 'prop-types';
import { Link, useStaticQuery, graphql } from 'gatsby';
import kebabCase from 'lodash/kebabCase';
import sortBy from 'lodash/sortBy';
import { createURL } from 'utils/createURL';
import { NEVER_NULL_TAG, MANAGED_FUSION_TAG, SPRINGBOARD_TAG, FUSION_CONNECTORS_TAG, FUSION_TAG, FUSION_SERVER_TAG, LUCIDWORKS_PLATFORM_TAG, productItemsPretty } from 'config/productItems';
import { SideBarContext, initialSideBarState, SideBarDispatchContext, UPDATE_GLOBAL_PRODUCT, UPDATE_GLOBAL_VERSION } from 'context/sidebarContextWrapper';
import SidenavFilter from 'components/Sidebar/SidenavFilter/SidenavFilter.component';
import NavItem from '../NavItem/NavItem.component';
import SearchMoreLinks from './SearchMoreLinks';
import { howToNav, active, sectionTitle} from './howto_styles.module.scss'


function HowToNav({ 
  path = '/', 
  currentLink, 
  currentProduct, 
  currentVersion,
  isGuide, 
  guideName,
  currentTag,
  pageData
}){

  const data = useStaticQuery(graphql`
    query {
      allAsciidoc(filter: {frontmatter: {layout: {eq: "howto"}}}) {
          edges {
            node {
              frontmatter {
                layout
                title
                product
                productVersion
                persona
                order
                objective
                permalink
                appType
                skip
              }
              paths {
                from {
                  source {
                    file
                  }
                }
              }
            }
          }
      }
    }
  `);


  const state = useContext(SideBarContext) || initialSideBarState;
  const dispatch = useContext(SideBarDispatchContext)

  useEffect(() => {
    const updateProduct = (newProductObject) => {
      dispatch({
        type: UPDATE_GLOBAL_PRODUCT,
        payload: newProductObject
      })
      dispatch({
        type: UPDATE_GLOBAL_VERSION,
        payload: newProductObject.label.replace(' ', '-')
      })
    }
  
    const pageProduct = pageData?.frontmatter?.product
    

    if(!pageProduct?.includes(state.currentProduct.tag)){
      if(pageProduct === NEVER_NULL_TAG){
          updateProduct({label: productItemsPretty[NEVER_NULL_TAG], value: productItemsPretty[NEVER_NULL_TAG], link: '/never-null/et383x/never-null', tag: NEVER_NULL_TAG})
      } else if(pageProduct === MANAGED_FUSION_TAG){
        updateProduct({label: '5.9', value: `${productItemsPretty[MANAGED_FUSION_TAG]} 5.9`, link: '/managed-fusion/5.9/ckqr0j/managed-fusion', tag: MANAGED_FUSION_TAG})
      } else if(pageProduct === SPRINGBOARD_TAG){
          updateProduct({label: "connected search", value: "connected search", link: "/springboard/connected-search/igtsjn/springboard", tag: SPRINGBOARD_TAG})
      } else if(pageProduct === LUCIDWORKS_PLATFORM_TAG){
        updateProduct({label: "Lucidworks Platform", value: "platform", link: "/lw-platform/platform/fh9alw/welcome-to-lucidworks-platform", tag: LUCIDWORKS_PLATFORM_TAG})
      } else if(pageProduct?.includes(FUSION_CONNECTORS_TAG)){
          updateProduct({label: 'Fusion Connectors', value: `Fusion Connectors`, link: `/fusion-connectors/2/fusion-connectors`, tag: FUSION_CONNECTORS_TAG})
      } else if(pageProduct?.includes(FUSION_SERVER_TAG) && /4./.test(state.currentVersion)){
          updateProduct({label: '4.2', value: `Fusion 4.2`, link: `/fusion-server/4.2/8764/fusion-server`, tag: FUSION_SERVER_TAG})
      } else if(pageProduct?.includes(FUSION_TAG)){
          updateProduct({label: '5.9', value: `Fusion 5.9`, link: `/fusion/5.9/6764/fusion`, tag: FUSION_TAG})
      }
    } 
    
  }, [ dispatch ]) // eslint-disable-line react-hooks/exhaustive-deps


  function addSubItem(subNavItems, node, splitPath, itemPath, index){
    const nodeTitle = node?.frontmatter?.title ?? 'No Title';
    const nodeUUID = node?.frontmatter?.permalink ??  '';
    const nodeOrder = node?.frontmatter?.order ?? 9999

    if (splitPath[index] === 'index') {
      const rootItem = subNavItems.find(item => item.path === `/${itemPath}`);
      if (rootItem) {
        rootItem.name = nodeTitle;
        rootItem.link = createURL(splitPath, nodeUUID) + `/${kebabCase(nodeTitle)}`; // eslint-disable-line prefer-template
        rootItem.order = nodeOrder;
      } else {
        subNavItems.push({
          name: nodeTitle,
          path: `/${itemPath}`,
          link: createURL(splitPath, nodeUUID) + `/${kebabCase(nodeTitle)}`, // eslint-disable-line prefer-template
          order: nodeOrder,
          subItems: [],
        });
      }
    } else if (index === splitPath.length) {
      if (node.frontmatter?.topindex) return;
      subNavItems.push({
        name: nodeTitle,
        path: `/${itemPath}`,
        link: createURL(splitPath, nodeUUID) + `/${kebabCase(nodeTitle)}`, // eslint-disable-line prefer-template
        order: nodeOrder, 
      });
    } else {
      const pathSegment = splitPath.slice(0, index).join('/');
      const indexPath = `/${pathSegment}/index`;
      const rootItem = subNavItems.find(item => item.path === indexPath);
      if (!rootItem) {
        subNavItems.push({ name: 'unknown', path: indexPath, subItems: [], active: false });
      }
      addSubItem(
        subNavItems.find(item => item.path === indexPath).subItems,
        node,
        splitPath,
        itemPath,
        index + 1,
        subNavItems.find(item => item.path === indexPath).link,
      );
    }
  };

  const getNavItems = (nodes) => {
    if(isGuide){
        const navList = []
        nodes.forEach(item => {
            const itemPath = item.node.paths.from.source.file;
            const splitPath = itemPath.split('/');
  
            // check if current node is a guide AND guide name of node matches current path
            if (splitPath.length >= 3 && splitPath[1] === guideName) {
              const index = 2;
              if (splitPath.length >= index && item.node.frontmatter.skip !== 'all') {
                addSubItem(navList, item.node, splitPath, itemPath, index)
              }
            }
        })
        return navList
    }

    // if page is not a guide, return related how-to docs
    return nodes.filter(item => {
      const itemPath = item.node.paths.from.source.file;
      const splitPath = itemPath.split('/')
      if(currentTag === NEVER_NULL_TAG || currentTag === SPRINGBOARD_TAG && splitPath.length <= 2){
        return item?.node?.frontmatter?.product?.includes(state.currentProduct.tag)
      }
      return item?.node?.frontmatter?.product?.includes(state.currentProduct.tag) && item?.node?.frontmatter?.productVersion?.includes(state.currentVersion) 
    })
   }

  const navHowTo = useMemo(() => {
    return getNavItems(data.allAsciidoc.edges)
  }, [currentProduct]) // eslint-disable-line react-hooks/exhaustive-deps

  const currentOrderedPersona = pageData?.frontmatter?.persona?.split(', ').sort().join()
  const currentOrderedObjective = pageData?.frontmatter?.objective.split(', ').sort().join()

  const relatedHowTos = navHowTo && !isGuide ? navHowTo.filter(item => {

        const orderedPersona = item?.node.frontmatter.persona?.split(', ').sort().join()
        const orderedObjective = item?.node.frontmatter.objective?.split(', ').sort().join()

        return (item?.node.frontmatter.skip !== 'all' 
          && pageData?.frontmatter?.permalink !== item?.node.frontmatter.permalink 
          && orderedPersona.includes(currentOrderedPersona) 
          && orderedObjective.includes(currentOrderedObjective)
          && item?.node.frontmatter.product.includes(pageData?.frontmatter.product)
        )
      } 
    ) : null

  return (
    <>
      <SidenavFilter howToRelated={navHowTo} version={state.currentVersion} />

      <Link to='/' activeClassName={active}>Home</Link>
      <Link to="/how-to" activeClassName={active}>How-to Guides</Link>
      { !isGuide && pageData?.frontmatter && <Link to={path} className={active}>{pageData?.frontmatter?.title}</Link>}

      {
        isGuide && <>
            <ul className={`topic-selector ${howToNav}`} style={{paddingLeft: 'unset'}}>
            {
                sortBy(navHowTo, 'order').map(navItem => (
                  <NavItem key={navItem.name} navItem={navItem} />
                )) 
            }
            </ul>
        </>
      }

      {
        relatedHowTos?.length >= 1 && <>
            <p className={sectionTitle}>Similar Articles</p>
            <ul className={`topic-selector ${howToNav}`} style={{paddingLeft: '1.1rem'}}>
            {
            relatedHowTos.slice(0,5).map(item => 
                  <li key={item.node.frontmatter.title}>
                    <Link 
                      to={`/how-to/${item.node.frontmatter.permalink}/${kebabCase(item.node.frontmatter.title)}`}
                    >
                      {item.node.frontmatter.title}
                    </Link>
                  </li>
                )
            }
            </ul>
        </>
      }

      {
        pageData?.frontmatter && 
          <>
            <p className={sectionTitle}>Discover More</p>
            <ul className={`topic-selector ${howToNav}`} style={{paddingLeft: '1.1rem'}}>
              <SearchMoreLinks
                currentTag={currentTag}
                currentVersion={currentVersion}
                pageData={pageData}
              />
            </ul>
          </>
      }

      <Link to={currentLink} style={{textTransform: 'capitalize'}}>{currentProduct}</Link>
    </>
  );
};

HowToNav.propTypes = {
  path: PropTypes.string,
  currentLink: PropTypes.string.isRequired,
  currentProduct: PropTypes.string.isRequired,
  currentVersion: PropTypes.string.isRequired,
  currentTag: PropTypes.string.isRequired,
  isGuide: PropTypes.bool.isRequired,
  guideName: PropTypes.string.isRequired,
  pageData: PropTypes.object.isRequired
};

export default HowToNav;
